import { LitElement, html } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { cartData, isEmpty, isCartLoading, CartItem } from "../store/cart-store";

/** Items visible in the list (excludes custom-sku fee items) */
function partitionItems(items: CartItem[]): {
  visible: CartItem[];
  customSkuByParent: Map<string, CartItem>;
} {
  const customSkuByParent = new Map<string, CartItem>();
  const visible: CartItem[] = [];

  for (const item of items) {
    const props = item.properties ?? {};
    if (props["_custom-sku-variant"]) {
      const parentVariant = props["_variant-to-customize"];
      if (parentVariant) {
        const yourText = props["Your Text"] ?? "";
        customSkuByParent.set(`${parentVariant}:${yourText}`, item);
      }
    } else {
      visible.push(item);
    }
  }

  return { visible, customSkuByParent };
}

/**
 * <cart-item-list>
 *
 * Renders the reactive list of cart items, or an empty-state message.
 * Lives inside the Liquid-rendered `<cart-drawer>` without owning it.
 *
 * Attributes (set in Liquid):
 *   data-empty-message  — translated empty cart string
 */
@customElement("cart-item-list")
export class CartItemList extends LitElement {
  @state() private _items: CartItem[] = [];
  @state() private _allItems: CartItem[] = [];
  @state() private _customSkuByParent = new Map<string, CartItem>();
  @state() private _isEmpty = true;
  @state() private _loading = false;

  private _emptyMessage = "";
  private _dealsUrl = "/collections/featured-deals";
  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._emptyMessage =
      this.dataset.emptyMessage || "Your cart is currently empty.";
    this._dealsUrl = this.dataset.dealsUrl || "/collections/featured-deals";

    this._disposeEffect = effect(() => {
      const allItems = cartData.value?.items ?? [];
      this._allItems = allItems;
      const { visible, customSkuByParent } = partitionItems(allItems);
      this._items = visible;
      this._customSkuByParent = customSkuByParent;
      this._isEmpty = isEmpty.value;
      this._loading = isCartLoading.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  render() {
    // First load — no items yet
    if (this._loading && this._items.length === 0) {
      return html`<div class="min-h-[120px] bg-gray-100/50 rounded"></div>`;
    }

    if (this._isEmpty) {
      return html`
        <div class="flex flex-wrap items-center justify-center text-center flex-1 h-full py-8">
          <div class="flex flex-col items-center">
            <svg
              id="Layer_2"
              xmlns="http://www.w3.org/2000/svg"
              class="icon icon-empty-cart block w-[140px] mx-auto mb-5"
              viewBox="0 0 126 125"
            >
              <defs>
                <clipPath id="clippath">
                  <circle cx="63.5" cy="62.5" r="62.5" />
                </clipPath>
              </defs>
              <g id="Layer_1-2">
                <g>
                  <circle fill="#e5e7eb" cx="62.5" cy="62.5" r="62.5" />
                  <g>
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M52.68,51.57l3.42,6.09" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M65.92,47.92v10.28" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M76.2,58.21l3.57-3.14" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M39.89,40.03v5.82" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M91.89,35.03v5.82" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M36.98,42.94h5.93" />
                    <path fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" d="M88.98,37.94h5.93" />
                    <circle fill="#9ca3af" cx="58.52" cy="29.5" r="3" />
                    <circle fill="#9ca3af" cx="79.52" cy="39.5" r="2" />
                    <circle fill="#9ca3af" cx="94.52" cy="57.5" r="1" />
                    <circle fill="#9ca3af" cx="40.02" cy="59" r="1.5" />
                  </g>
                  <g clip-path="url(#clippath)">
                    <path
                      fill="#9ca3af"
                      d="M43.69,157.14c-4.63,0-8.4-3.77-8.4-8.4s3.77-8.4,8.4-8.4,8.4,3.77,8.4,8.4-3.77,8.4-8.4,8.4Zm44.48-.04c-4.63,0-8.4-3.75-8.4-8.35s3.77-8.35,8.4-8.35,8.4,3.75,8.4,8.35-3.77,8.35-8.4,8.35Zm8.4-22.21H43.69c-2.98,0-5.67-1.53-7.2-4.1-1.54-2.59-1.59-5.72-.15-8.38l6-10.85,.12-.22-.11-.23-16.01-33.77-.14-.29h-8.71v-7.9h13.73l4.04,8.62,.13,.29H101.56c1.41,0,2.68,.73,3.39,1.95,.71,1.22,.73,2.67,.04,3.89l-15.92,28.88c-1.46,2.67-4.28,4.33-7.35,4.33H48.28l-.14,.26-4.89,8.9-.41,.74h53.73v7.9Zm-57.41-48.95l.34,.71,10.54,22.25,.14,.29h31.84l.14-.26,12.28-22.25,.41-.74H39.16Z"
                    />
                  </g>
                </g>
              </g>
            </svg>
            <span class="font-semibold block mb-5">${this._emptyMessage}</span>
            <a class="btn" href=${this._dealsUrl}>See Deals</a>
          </div>
        </div>
      `;
    }

    return html`
      <div class="flex flex-col gap-2.5 pt-2.5${this._loading ? " opacity-60" : ""}">
        ${repeat(
          this._items,
          (item) => item.key,
          (item) => {
            // Line index must reference the position in the FULL items array (1-based)
            const line = this._allItems.indexOf(item) + 1;
            const yourText = (item.properties ?? {})["Your Text"] ?? "";
            const customSkuItem = yourText
              ? (this._customSkuByParent.get(`${item.variant_id}:${yourText}`) ?? null)
              : null;
            const customSkuLine = customSkuItem
              ? this._allItems.indexOf(customSkuItem) + 1
              : 0;
            return html`
              <cart-item-row
                .item=${item}
                .line=${line}
                .customSkuItem=${customSkuItem}
                .customSkuLine=${customSkuLine}
              ></cart-item-row>
            `;
          }
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-item-list": CartItemList;
  }
}
